import {ChangeDetectionStrategy, Component, OnInit, ViewChild} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Subscription} from "rxjs";
import {map} from "rxjs/operators";
import {Utils} from "@app/utilities/Utils";
import {PortalUser} from "@app/model/portal-user.model";
import {MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {GlobalSpinnerService} from "@app/services/misc-services/global-spinner.service";
import {CreditUnionService} from "@app/services/credit-union/credit-union.service";
import {AddUserComponent} from "@app/pages/admin/add-user/add-user.component";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {AlertsService} from "@app/utilities/alerts.service";
import {LoginService} from "@app/login/login.service";
import {AdminFormHelperService} from "@app/pages/admin/admin-form-helper.service";
import {UpdateUser} from "@app/pages/admin/update-user";
import {ExportTableComponent} from "@app/commons/export-table/export-table.component";
import {ConfirmationDialogService} from "@app/commons/confirmation-dialog/confirmation-dialog.service";

@Component({
    selector: 'flex-admin',
    templateUrl: './admin.component.html',
    styleUrls: ['./admin.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminComponent implements OnInit {

    activeCreditUnion: CreditUnion = new CreditUnion();
    activeCreditUnionPortalUsers: BehaviorSubject<PortalUser[]> = new BehaviorSubject<PortalUser[]>([]);
    selectedPortalUserInfo: PortalUser = new PortalUser();
    mode: 'list' | 'edit' | 'table' = 'list';
    searchFilter: string = '';
    userSearchFilter: string = '';
    creditUnions: BehaviorSubject<CreditUnion[]> = new BehaviorSubject<CreditUnion[]>([]);
    selectedRole: 'superAdmin' | 'admin' | 'general' | 'limited' = "limited"
    AddUserSubscription: Subscription;
    roles: Array<{ value: string, label: string }> = [
        {
            label: 'Super Admin',
            value: 'superAdmin'
        }, {
            label: 'Admin',
            value: 'admin'
        }, {
            label: 'General',
            value: 'general'
        }, {
            label: 'Limited',
            value: 'limited'
        }]

    addUserDialogRef: MatDialogRef<AddUserComponent>;
    addUserDialogConfig = new MatDialogConfig();
    addUserSubscription: Subscription;
    exportTableDialogRef: MatDialogRef<ExportTableComponent>;
    exportTableDialogConfig = new MatDialogConfig();
    dataSource: MatTableDataSource<any> = new MatTableDataSource<any>([]);
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    columns: string[] = ['username', 'firstName', 'lastName', 'title', 'cuid', 'email', 'expirationDate'];
    displayedColumns: Array<{ label: string, value: string }> = [
        {label: 'Username', value: 'username'},
        {label: 'First Name', value: 'firstName'},
        {label: 'Last Name', value: 'lastName'},
        {label: 'Title', value: 'title'},
        {label: 'CUID', value: 'cuid'},
        {label: 'Email Address', value: 'email'},
        {label: 'Expiration Date', value: 'expirationDate'}];

    constructor(private http: HttpClient, private globalSpinnerService: GlobalSpinnerService, private creditUnionService: CreditUnionService,
                public dialog: MatDialog, public snackBar: AlertsService, public loginService: LoginService, public FormHelper: AdminFormHelperService,
                private confirmservice: ConfirmationDialogService) {
    }

    ngOnInit(): void {
        this.mode = 'list';
        this.creditUnionService.getAllCreditUnions().subscribe((creditUnions: CreditUnion[]) =>
            this.creditUnions.next(creditUnions))
    }

    onSearchChange(value: string) {
        if (value.toLowerCase() === 'all') {
            this.mode = 'table';
            this.globalSpinnerService.addEvent('getAllContacts', 'Get All Contacts Info');
            this.http.get('/api/secured/creditUnion/getAllContacts').subscribe((users: PortalUser[]) => {
                this.dataSource.data = users;
                this.dataSource.paginator = this.paginator;
                this.dataSource.sort = this.sort;
                this.globalSpinnerService.removeEvent('getAllContacts');
            })
        } else if (value.length === 3) {
            const creditUnion: CreditUnion = this.creditUnions.getValue()
                .find(cu => cu.cuid.toLowerCase() === value.toLowerCase())
            if (creditUnion) {
                this.selectActiveCreditUnion(creditUnion);
                this.mode = 'edit';
            } else {
                this.activeCreditUnionPortalUsers.next([]);
                this.selectedPortalUserInfo = new PortalUser();
            }
        } else {
            this.mode = 'list';
        }
    }

    selectActiveCreditUnion(cu: CreditUnion) {
        this.activeCreditUnionPortalUsers.next([]);
        if (cu) {
            this.activeCreditUnion = cu;
            this.mode = 'edit';
            this.searchFilter = cu.cuid;
            this.refreshUsers();
        }
    }

    refreshUsers() {
        this.selectedPortalUserInfo = new PortalUser();
        this.http.post('/api/secured/creditUnion/getAllPortalUsersByCuid', {cuid: this.searchFilter})
            .pipe(map((creditUnions: CreditUnion[]) => Utils.orderBy(creditUnions, 'firstName')))
            .subscribe((users: PortalUser[]) => {
                this.activeCreditUnionPortalUsers.next(users);
                if (!this.selectedPortalUserInfo?.username) {
                    this.selectedPortalUserInfo = users[0];
                }
            })
    }

    onClickAllPortalUserRow(user: PortalUser) {
        if (user?.cuid) {
            const creditUnion: CreditUnion = this.creditUnions.getValue().find(cu => cu.cuid === user.cuid);
            this.selectActiveCreditUnion(creditUnion);
            this.selectedPortalUserInfo = user;
        }
    }

    onPortalUserChange(user: PortalUser) {
        this.selectedPortalUserInfo = user;
    }

    getRole(user: PortalUser) {
        switch (true) {
            case user?.superAdmin:
                return 'superAdmin';
            case user?.admin:
                return 'admin';
            case user?.general:
                return 'general';
            case user?.limited:
                return 'limited';
        }
        return ""
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
        if (this.dataSource.paginator) {
            this.dataSource.paginator.firstPage();
        }
    }

    addUser() {
        this.addUserDialogConfig.data = {CU: this.activeCreditUnion.cuid, ref: this.addUserDialogRef}
        this.addUserDialogConfig.width = '800px';
        this.addUserDialogConfig.height = '750px';
        this.addUserDialogRef = this.dialog.open(AddUserComponent, this.addUserDialogConfig);
        this.AddUserSubscription = this.addUserDialogRef.componentInstance.userAdded.subscribe(() => {
            this.refreshUsers();
        })
    }

    deleteUser() {
        this.confirmservice.openDialog('Warning', 'Are you sure you want to delete ' + this.selectedPortalUserInfo.firstName +
            ' ' + this.selectedPortalUserInfo.lastName + '?').subscribe(dialogResult => {
            if (dialogResult) {
                let Error: string;
                this.http.post(`/api/secured/admin/deleteUser/${this.selectedPortalUserInfo.id}`, {}).subscribe(() => {
                        this.snackBar.getAlert(`User Deleted`, 'X')
                        this.refreshUsers();
                    },
                    error => {
                        Error = error.status + ' ' + error.statusText;
                        this.snackBar.getError('Error: ' + Error, 'X')
                    });
            }
        })
    }


    editUser() {
        const updateUser: UpdateUser = {
            id: this.selectedPortalUserInfo.id,
            username: this.FormHelper.UserForm.value.username,
            fName: this.FormHelper.UserForm.value.firstname,
            lName: this.FormHelper.UserForm.value.lastname,
            email: this.FormHelper.UserForm.value.emailAddress,
            phone: this.FormHelper.UserForm.value.phonenumber,
            role: this.FormHelper.UserForm.value.role,
            title: this.FormHelper.UserForm.value.title,
            expDate: this.FormHelper.UserForm.value.experationdate,
            createdDate: this.FormHelper.UserForm.value.createddate,
            lastLoginDate: this.FormHelper.UserForm.value.lastlogindate
        };
        let Error: string;
        this.http.post(`/api/secured/admin/editUser`, updateUser).subscribe(() => {
                this.snackBar.getAlert(`User Updated`, 'X')
                this.refreshUsers();
            },
            error => {
                Error = error.status + ' ' + error.statusText;
                this.snackBar.getError('Error: ' + Error, 'X')
            });
    }

    resetPassword() {
        this.confirmservice.openDialog('Warning', 'Are you sure you want to reset ' + this.selectedPortalUserInfo.firstName +
        ' ' + this.selectedPortalUserInfo.lastName + "'s password?").subscribe(dialogResult => {
        if (dialogResult) {
            let Error: string;
            this.http.get(`/api/unsecured/creditUnion/forgotPassword/${this.selectedPortalUserInfo.emailAddress}`, {})
                .subscribe(() => {
                        this.snackBar.getAlert(`Reset Email Sent`, 'X')
                    },
                    error => {
                        Error = error.error.text;
                        if (Error !== "success") {
                            Error = error.status + ' ' + error.statusText;
                            this.snackBar.getError('Error: ' + Error, 'X');
                        } else {
                            this.snackBar.getAlert(`Reset Email Sent`, 'X');
                        }
                    })
        }
    })

    }

    export() {
        this.exportTableDialogConfig.data = {
            displayedColumns: this.displayedColumns,
            selectedUsers: this.dataSource.filteredData
        }
        this.exportTableDialogConfig.width = '1000px';
        this.exportTableDialogConfig.height = 'auto';
        this.exportTableDialogRef = this.dialog.open(ExportTableComponent, this.exportTableDialogConfig);
    }
}

export class CreditUnion {
    addressLine1: string;
    addressLine2: string;
    assets: number;
    attn: string;
    charterNum: string;
    charterType: string;
    city: string;
    cuid: string;
    deconversionDate: number;
    errors: string;
    ext: string;
    fax: string;
    faxExt: string;
    flexRelease: string;
    flexReleaseDate: string;
    formerManager: string;
    formerVendor: string;
    id: number;
    info: CreditUnionInfo;
    installDate: number;
    installer: string;
    locations: string;
    manager: string;
    marketingRep: string;
    members: number;
    name: string;
    nightExt: string;
    nightPhone: string;
    phone: string;
    prefName: string;
    state: string;
    tcp1: string;
    tcp2: string;
    tcp3: string;
    tcp4: string;
    version: null
    zip1: string;
}

export class CreditUnionInfo {
    cuid: string;
    errors: string;
    guiType: string;
    id: null
    processor: string;
    statement: string;
    version: null
    webSite: string;
}

